import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { KeyRound, UserRound } from 'lucide-react'

import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Separator } from '@/components/ui/separator'

import {
  requestProfile,
  requestChangePassword,
  requestResetChangePassword
} from '../../stores/auth'

// Cognito password policy of the Station user pools (min 8, lowercase, number, symbol)
const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: v => v.length >= 8 },
  { label: 'A lowercase letter', test: v => /[a-z]/.test(v) },
  { label: 'A number', test: v => /[0-9]/.test(v) },
  { label: 'A symbol', test: v => /[^A-Za-z0-9]/.test(v) },
]

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words text-sm">{value || <span className="text-muted-foreground">—</span>}</dd>
    </div>
  )
}

function Profile({
  username,
  contactName,
  contactEmail,
  accountName,
  profile,
  changePassword,
  requestProfile,
  requestChangePassword,
  requestResetChangePassword,
}) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  useEffect(() => {
    requestProfile()
    requestResetChangePassword()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Clear the form after a successful change
  useEffect(() => {
    if (changePassword.success) {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    }
  }, [changePassword.success])

  const attrs = profile.attributes || {}
  const displayName = attrs.name || [attrs.given_name, attrs.family_name].filter(Boolean).join(' ') || contactName
  const email = attrs.email || contactEmail

  const rulesMet = PASSWORD_RULES.every(r => r.test(newPassword))
  const matches = newPassword.length > 0 && newPassword === confirmPassword
  const canSubmit = currentPassword.length > 0 && rulesMet && matches && !changePassword.loading

  const onSubmit = e => {
    e.preventDefault()
    if (canSubmit) requestChangePassword(currentPassword, newPassword)
  }

  return (
    <div className="mx-auto grid w-full max-w-5xl grid-cols-1 gap-3 lg:grid-cols-2">
      {/* ── Profile (read only) ── */}
      <Card className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <UserRound className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold leading-tight">Profile</h2>
        </div>

        {!profile.loaded ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Spinner size={18} /> Loading your profile…
          </div>
        ) : (
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Name" value={displayName} />
            <Field label="Email" value={email} />
            <Field label="Username" value={username || attrs.preferred_username} />
            <Field label="Phone" value={attrs.phone_number} />
            <div className="sm:col-span-2">
              <Field label="Account" value={accountName} />
            </div>
          </dl>
        )}

        <Separator className="my-4" />
        <p className="text-xs text-muted-foreground">
          These details are managed by Aberdeen. To update your name, email or account, contact your Aberdeen account manager.
        </p>
      </Card>

      {/* ── Change password ── */}
      <Card className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold leading-tight">Change Password</h2>
        </div>

        {changePassword.success && (
          <Alert variant="success" className="mb-3">Your password has been changed.</Alert>
        )}
        {changePassword.hasError && (
          <Alert variant="error" className="mb-3">{changePassword.error}</Alert>
        )}

        <form onSubmit={onSubmit} className="space-y-3" autoComplete="off">
          <div className="space-y-1.5">
            <Label htmlFor="current-password">Current password</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              disabled={changePassword.loading}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              disabled={changePassword.loading}
            />
            <ul className="grid grid-cols-1 gap-x-4 gap-y-0.5 text-xs sm:grid-cols-2">
              {PASSWORD_RULES.map(r => {
                const ok = r.test(newPassword)
                return (
                  <li key={r.label} className={ok ? 'text-success' : 'text-muted-foreground'}>
                    {ok ? '✓' : '•'} {r.label}
                  </li>
                )
              })}
            </ul>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              disabled={changePassword.loading}
            />
            {confirmPassword.length > 0 && !matches && (
              <p className="text-xs text-destructive">The passwords do not match.</p>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="submit" disabled={!canSubmit}>
              {changePassword.loading ? <><Spinner size={14} /> Changing…</> : 'Change password'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}

const mapStateToProps = state => {
  const {
    username,
    contactName,
    contactEmail,
    accountName,
    profile,
    changePassword
  } = state.stores.auth

  return {
    username,
    contactName,
    contactEmail,
    accountName,
    profile,
    changePassword
  }
}

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators({
    requestProfile,
    requestChangePassword,
    requestResetChangePassword
  }, dispatch)
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Profile))
